import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateBank = () => {
  const [banks, setBanks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ bank_name: '', account_number: '', branch: '', ifsc_code: '', account_type: 'Savings', opening_balance: 0 });

  useEffect(() => { fetchBanks(); }, []);

  const fetchBanks = async () => { 
    try { 
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/accounts/banks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }); 
      if (response.status === 200 || response.data) {
        setBanks(await response.json()); 
      }
    } catch (error) { 
      console.error('Error:', error); 
    } 
  };

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    setLoading(true); 
    try { 
      const token = localStorage.getItem('token');
      const url = editingBank ? `${API_URL}/api/accounts/banks/${editingBank.id}` : `${API_URL}/api/accounts/banks`; 
      const method = editingBank ? 'PUT' : 'POST'; 
      const response = await fetch(url, { 
        method, 
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }, 
        body: JSON.stringify(formData) 
      }); 
      if (response.status === 200 || response.data) { 
        alert('Bank saved successfully!'); 
        fetchBanks(); 
        handleCloseModal(); 
      } else {
        const error = await response.text();
        alert(`Failed to save bank: ${error}`);
      }
    } catch (error) { 
      alert('Failed to save bank'); 
      console.error(error);
    } finally { 
      setLoading(false); 
    } 
  };
  const handleDelete = async (id) => { 
    if (window.confirm('Are you sure you want to delete this bank?')) { 
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/accounts/banks/${id}`, { 
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      }); 
      fetchBanks(); 
    } 
  };
  const handleEdit = (bank) => { setEditingBank(bank); setFormData({ bank_name: bank.bank_name, account_number: bank.account_number, branch: bank.branch, ifsc_code: bank.ifsc_code || '', account_type: bank.account_type, opening_balance: bank.opening_balance }); setIsModalOpen(true); };
  const handleCloseModal = () => { setIsModalOpen(false); setEditingBank(null); setFormData({ bank_name: '', account_number: '', branch: '', ifsc_code: '', account_type: 'Savings', opening_balance: 0 }); };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Bank Accounts</CardTitle><Button onClick={() => setIsModalOpen(true)}><Plus className="w-4 h-4 mr-2" />Add Bank</Button></CardHeader>
        <CardContent>
          <table className="w-full border-collapse">
            <thead><tr className="border-b"><th className="text-left p-3">Bank Name</th><th className="text-left p-3">Account Number</th><th className="text-left p-3">Branch</th><th className="text-left p-3">IFSC</th><th className="text-left p-3">Type</th><th className="text-left p-3">Balance</th><th className="text-left p-3">Actions</th></tr></thead>
            <tbody>{banks.length === 0 ? <tr><td colSpan="7" className="text-center p-4 text-gray-500">No banks found.</td></tr> : banks.map((bank) => (<tr key={bank.id} className="border-b hover:bg-gray-50"><td className="p-3 font-medium">{bank.bank_name}</td><td className="p-3">{bank.account_number}</td><td className="p-3">{bank.branch}</td><td className="p-3">{bank.ifsc_code}</td><td className="p-3">{bank.account_type}</td><td className="p-3">₹{bank.current_balance}</td><td className="p-3"><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => handleEdit(bank)}><Edit className="w-3 h-3" /></Button><Button size="sm" variant="destructive" onClick={() => handleDelete(bank.id)}><Trash2 className="w-3 h-3" /></Button></div></td></tr>))}</tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>{editingBank ? 'Edit' : 'Add'} Bank</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4"><div><Label>Bank Name *</Label><Input required value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} /></div><div><Label>Account Number *</Label><Input required value={formData.account_number} onChange={(e) => setFormData({ ...formData, account_number: e.target.value })} /></div></div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Branch *</Label><Input required value={formData.branch} onChange={(e) => setFormData({ ...formData, branch: e.target.value })} /></div><div><Label>IFSC Code</Label><Input value={formData.ifsc_code} onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })} /></div></div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Account Type *</Label><Select value={formData.account_type} onValueChange={(value) => setFormData({ ...formData, account_type: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Savings">Savings</SelectItem><SelectItem value="Current">Current</SelectItem></SelectContent></Select></div><div><Label>Opening Balance *</Label><Input type="number" required value={formData.opening_balance} onChange={(e) => setFormData({ ...formData, opening_balance: parseFloat(e.target.value) })} /></div></div>
            <DialogFooter><Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button><Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateBank;